/**
 * nav-rail — ГЛОБАЛЬНЫЙ навигационный рейл-оболочка (app-shell) для всех
 * не-контекстных экранов + вход. ОДИН переиспользуемый модуль (как board-view) —
 * экраны монтируются в его `contentSlot`, рейл живёт всегда, не пересоздаётся.
 * Спека: docs/specs/nav-rail.md.
 *
 * Инвариант owner'а (verbatim): «доска не делится местом» — рейл НИКОГДА не ужимает
 * доску. Реализация: доска центрируется в полноширинном контенте (justify-content
 * на screen-root), поэтому слева от неё остаётся ПУСТОЕ поле. Рейл — `position:fixed`
 * НАД этим пустым полем (оверлей), доска под ним не трогается. Видимость считается
 * ФОРМУЛОЙ от запаса слева, НЕ брейкпоинтами:
 *
 *   leftFree = (clientWidth − boardW) / 2          // пустой левый отступ доски
 *   inline   ⇔  leftFree ≥ railW + m               // рейл влезает, не касаясь доски
 *
 * boardW берётся из той же геометрии, что и board-view (--cell-size — JS-owned px),
 * railW измеряется у самого элемента (getBoundingClientRect) → без пиннинга констант.
 * Когда не влезает — рейл сворачивается в кнопку ☰ и выезжает оверлеем с затемнением
 * (transform-transition, дёшево). Пересчёт на событие viewport-sizer'а + resize.
 *
 * Модульность: рейл — generic (`items[]` + account-slot); экран/демо задаёт пункты и
 * действия. Иконки — inline Tabler-вектор (currentColor), размеры формулами от
 * --fs-control (как иконка-дверь выхода). CSS — co-located styles/nav-rail.css.
 *
 * Account-slot (account-profile-redesign.md §2): ОДИН state-driven слот СВЕРХУ рейла,
 * сразу под брендом (owner-directive 2026-06-12 — вход/чип первым делом под «Lines 98»).
 * Гость (`account === null`) → login-пилюля (клик → `onLogin`, открывает auth-modal).
 * Залогинен (`account` задан) → account-chip (иконка-«вошёл» + ник + меню logout).
 * Слот пере-рендерится через `setAccountState(identity | null)` (handle) — рейл-
 * singleton не пересоздаётся (§2.3, закрытие state-blind бага). Прежняя статичная
 * `.nav-item--login` + login-попап с провайдерами УДАЛЕНЫ: провайдеры теперь живут
 * внутри auth-panel в auth-modal (§2.1 / §4).
 */

import '../styles/nav-rail.css';
import '../styles/nav-lang.css';

import {
  UI_LANGS,
  LANG_LABELS,
  LANG_FLAG,
  HREFLANG_TAG,
  DEFAULT_CONTENT_LANG,
} from '@lines98/shared/config/seo';

import { mountAccountChip, type AccountChipHandle } from '../components/account-chip.js';
import { createIconButton } from '../components/icon-button.js';
import { createPillButton } from '../components/pill-button.js';
import { useGameStore } from '../store/game-store.js';
import { BRAND_LOGO_SVG } from './brand-logo.js';
import { navigateContent } from './content-routing.js';
import { parseContentPath } from '@lines98/shared/config/content-routes';
import { attachDrawer } from './drawer.js';
import { flagSvg } from './i18n/flags.js';
import { applyLocale, setLocale, t } from './i18n/index.js';
import { localeFromNavigatorLanguage } from './i18n/resolve-locale.js';
import { mountNavLang, type NavLangHandle } from './nav-lang.js';
import { VIEWPORT_APPLIED_EVENT } from './viewport-sizer.js';

// =============================================================================
// Иконки — Tabler outline (24×24, stroke=currentColor). Размер задаёт CSS (em).
// Логотип бренда — НЕ здесь: настоящий логотип в едином источнике lib/brand-logo
// (BRAND_LOGO_SVG), общем с SSG (прежняя 3-кругов-заглушка удалена — тикет AC-11).
// =============================================================================

const ICON_CALENDAR = `
  <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"/>
  <path d="M16 3v4"/><path d="M8 3v4"/><path d="M4 11h16"/><path d="M11 15h1"/><path d="M12 15v3"/>
`;

// Canonical Tabler «trophy» — ручки = окружности с центром (5,9)/(19,9) r2,
// крайний x=3 (внутри viewBox 0..24) → stroke не упирается в край и не обрезается
// (см. инцидент 896×414: самодельная дуга вылезала на x≈1 и срезалась overflow).
const ICON_TROPHY = `
  <path d="M8 21h8"/><path d="M12 17v4"/><path d="M7 4h10"/>
  <path d="M17 4v8a5 5 0 0 1 -10 0v-8"/>
  <path d="M5 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
  <path d="M19 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
`;

const ICON_PLAY = `<path d="M7 4v16l13 -8z"/>`;

const ICON_USER = `
  <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/>
  <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/>
`;

const ICON_SETTINGS = `
  <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z"/>
  <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/>
`;

const ICON_LOGIN = `
  <path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"/>
  <path d="M21 12h-13l3 -3"/><path d="M11 15l-3 -3"/>
`;

const ICON_BURGER = `<path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/>`;

// Canonical Tabler «book» — раскрытая книга: контент-страницы (правила/гайд/FAQ —
// «узнать о игре»). Внутри viewBox 0..24 (x∈[3,21]) → stroke не упирается в край.
const ICON_BOOK = `
  <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/>
  <path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0"/>
  <path d="M3 6l0 13"/><path d="M12 6l0 13"/><path d="M21 6l0 13"/>
`;

// Папка + треугольник Play — «Архив игр» (записи сыгранных партий, owner 2026-06-24).
// Папка = коллекция/хранилище, play-треугольник = это записи партий (реплеи), отличает
// от книжной иконки контент-страниц (Help остаётся на ICON_BOOK). Tabler-folder силуэт;
// внутри viewBox 0..24 (x∈[3,21]) → stroke не упирается в край (как остальные).
const ICON_GAME_ARCHIVE = `
  <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2"/>
  <path d="M10.5 11.5l4.5 2.5l-4.5 2.5z"/>
`;

// Canonical Tabler «diamond» — гранёный камень: «Ювелир»-мета (мастерская §6.2).
// Внутри viewBox 0..24 (x∈[3,21]) → stroke не упирается в край (как trophy).
const ICON_GEM = `
  <path d="M6 5h12l3 5l-8.5 9.5a0.7 .7 0 0 1 -1 0l-8.5 -9.5z"/>
  <path d="M10 12l-2 -2.2l1.5 -4.8"/><path d="M12 5v14"/>
  <path d="M3 10h18"/>
`;

// Гном-колпак + лицо — gnomes-мета (meta-gnomes.md §5.1): островершинный колпак над
// бородатым лицом, узнаваемый силуэт гнома-добытчика, отличим от diamond мастерской.
// Внутри viewBox 0..24 (x∈[5,19]) → stroke не упирается в край (как trophy/gem).
const ICON_GNOME = `
  <path d="M12 3l6.5 9.5h-13z"/>
  <path d="M7 12.5a5 5 0 0 0 10 0"/>
  <path d="M10 19.5h4"/>
`;

// Tabler «heart» (outline) — пункт «♥ Поддержать» (крипто-донат). Внутри viewBox
// 0..24 (x∈[~3,21]) → stroke не упирается в край (как trophy/gem/gnome).
const ICON_HEART = `
  <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.572a5 5 0 1 1 7.5 6.572" />
`;

/** Известные иконки рейла — экран ссылается по ключу (не вставляет SVG руками). */
export type NavIconKey =
  | 'calendar'
  | 'trophy'
  | 'play'
  | 'user'
  | 'settings'
  | 'login'
  | 'workshop'
  | 'gnomes'
  | 'book'
  | 'archive'
  | 'heart';

const ICONS: Record<NavIconKey, string> = {
  calendar: ICON_CALENDAR,
  trophy: ICON_TROPHY,
  play: ICON_PLAY,
  user: ICON_USER,
  settings: ICON_SETTINGS,
  login: ICON_LOGIN,
  workshop: ICON_GEM,
  gnomes: ICON_GNOME,
  book: ICON_BOOK,
  archive: ICON_GAME_ARCHIVE,
  heart: ICON_HEART,
};

/** Обернуть path-набор в <svg> с общими атрибутами (fill icons — особый случай). */
function svg(paths: string, opts?: { filled?: boolean }): string {
  const fill = opts?.filled ? 'currentColor' : 'none';
  return `<svg class="nav-icon" viewBox="0 0 24 24" fill="${fill}" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths}</svg>`;
}

/**
 * Готовая `<svg>`-разметка login-иконки рейла (single source). Экран личного кабинета
 * строит свою login-плашку ТЕМ ЖЕ примитивом `createPillButton` и берёт ОТСЮДА тот же
 * SVG → визуально идентична рейловой login-CTA, НЕ своя иконка (тикет fe-profile §4
 * REUSE / AC-7). Это ТОЛЬКО иконка — подпись резолвится потребителем через каталог t().
 */
export const LOGIN_ICON_SVG = svg(ICON_LOGIN);

// =============================================================================
// API
// =============================================================================

export interface NavRailItem {
  /** Стабильный id (для active-состояния). */
  readonly id: string;
  /**
   * Каталог-КЛЮЧ подписи (T-I18N-RAIL): пункт несёт ключ (`game.play` /
   * `game.rail.workshop`), НЕ готовую строку — иначе подпись не была бы реактивной
   * к смене языка (AC-1/AC-2). Рейл резолвит `t(labelKey)` при рендере И перерисовывает
   * label-узлы по сохранённым ключам при смене locale (subscribe, без пересоздания).
   */
  readonly labelKey: string;
  /** Ключ иконки. */
  readonly icon: NavIconKey;
  /** Неактивный пункт-заглушка («скоро»). */
  readonly disabled?: boolean;
  /** Маленький бейдж справа (напр. «1/день»). */
  readonly badge?: string;
  /** Действие при выборе (для disabled игнорируется). */
  readonly onSelect?: () => void;
}

/**
 * Идентичность залогиненного для account-slot (§2.2). Минимум для chip: id (цвет
 * аватара) + ник (монограмма + подпись). `null` идентичности = гость (login-пилюля).
 */
export interface NavRailAccountIdentity {
  readonly playerId: string;
  readonly displayName: string;
}

/**
 * Колбэки account-slot (§2/§3) — инъектируются bootShell'ом (рейл их не реализует).
 *   - onLogin: login-пилюля (гость) → открыть auth-modal (§2.1).
 *   - onLogout: меню chip «Выйти» (§3.1) → ОДНА общая logout-функция (не копия).
 * Пункт профиля — отдельный пункт nav-list (свой `onSelect`), НЕ колбэк чипа (owner-
 * directive 2026-06-12: меню чипа = только logout).
 */
export interface NavRailAccountCallbacks {
  readonly onLogin: () => void;
  readonly onLogout: () => void | Promise<void>;
}

/**
 * Утилитарная кнопка внизу рейла (отделена линией) — НЕ пункт-
 * направление: иконка без подписи, открывает оверлей (напр. шестерёнка settings,
 * §5.5). НЕ проходит через `beforeNavigate`/leave-guard (это не навигация → во время
 * партии ничего не спрашивает). Owner-decision 2026-06-04.
 */
export interface NavRailUtility {
  readonly id: string;
  readonly icon: NavIconKey;
  /**
   * Каталог-КЛЮЧ accessible-label'а (T-I18N-RAIL) — видимой подписи нет (только иконка),
   * но aria/title локализуются и обновляются при смене языка (AC-5). Напр. `game.settings`.
   */
  readonly ariaLabelKey: string;
  readonly onSelect: () => void;
}

export interface NavRailOptions {
  /** Пункты навигации (порядок = порядок отображения). */
  readonly items: readonly NavRailItem[];
  /** id активного пункта (подсветка). */
  readonly activeId?: string;
  /**
   * Account-slot (§2): стартовая идентичность — `null` (гость → login-пилюля) или
   * залогинен (→ account-chip). Пере-рендер через `handle.setAccountState`.
   */
  readonly account?: NavRailAccountIdentity | null;
  /** Колбэки account-slot (вход / профиль / выход — §2/§3). */
  readonly accountCallbacks?: NavRailAccountCallbacks;
  /** Утилитарные кнопки внизу рейла (иконки без подписей; напр. шестерёнка). */
  readonly utilities?: readonly NavRailUtility[];
  /**
   * Гейт перед навигацией по пункту: вернёт false → клик отменяется ПОЛНОСТЬЮ
   * (без смены active, без закрытия drawer, без onSelect). Используется leave-guard'ом
   * (подтверждение выхода из запущенной игры) — поэтому проверка ДО setActive, иначе
   * подсветка успела бы перескочить на отменённый пункт.
   */
  readonly beforeNavigate?: (itemId: string) => boolean | Promise<boolean>;
  /**
   * Клик по бренд-логотипу (слева-вверху) → лендинг (ADR-0020 §«Рейл как единая
   * оболочка»: «Логотип кликабелен → лендинг»). Если задан — бренд становится
   * кнопкой; иначе остаётся неинтерактивным. Закрывает drawer как обычная нав.
   */
  readonly onBrandSelect?: () => void;
}

export interface NavRailHandle {
  /** Контейнер, в который экраны монтируют свой DOM (host для mount*Screen). */
  readonly contentSlot: HTMLElement;
  /** Сменить активный пункт (подсветка). `null` — снять подсветку (напр. на result/replay). */
  setActive(id: string | null): void;
  /**
   * Пере-рендерить account-slot под новую идентичность (§2.3): `null` → login-пилюля
   * (гость); идентичность → account-chip. Вызывается после login/register/logout без
   * пересоздания рейла (он app-shell singleton). Закрывает state-blind баг.
   */
  setAccountState(identity: NavRailAccountIdentity | null): void;
  /**
   * Выставить/снять dot-индикатор «есть новое» на пункте рейла (helper-unlock A2, AC-11).
   * Dot — вариант существующего `.nav-item-badge` (`.nav-item-badge--dot`), задаётся через
   * рейл-handle (рейл-singleton НЕ пересоздаётся — как `setActive`/`setAccountState`),
   * НЕ bespoke indicator-div. Виден и в inline-, и в collapsed-рейле (dot живёт на самой
   * кнопке-пункте). `kind='dot'` → показать; `null` → снять. `ariaLabel` (локализованный)
   * добавляется к accessible-имени пункта пока dot активен (смысл не только цветом, AC-13);
   * при снятии aria-добавка убирается.
   *
   * @param id        — id пункта (`'gnomes'` и т.п.).
   * @param kind      — `'dot'` показать индикатор, `null` снять.
   * @param ariaLabel — локализованная aria-добавка «есть новые помощники» (только для 'dot').
   */
  setItemBadge(id: string, kind: 'dot' | null, ariaLabel?: string): void;
  detach(): void;
}

/**
 * Смонтировать app-shell рейл в `host`. Возвращает `contentSlot` — host для экранов.
 */
export function mountNavRail(host: HTMLElement, opts: NavRailOptions): NavRailHandle {
  host.innerHTML = '';

  // --- Контент-слот: полноширинный, экраны рендерятся сюда (доска центрируется). ---
  const content = document.createElement('div');
  content.className = 'nav-content';

  // --- Затемнитель (drawer-режим). ---
  const scrim = document.createElement('div');
  scrim.className = 'nav-scrim';
  scrim.setAttribute('aria-hidden', 'true');

  // --- Рейл. ---
  // a11y-строки (aria/title) приходят из каталога i18n через t() (T-I18N-RAIL AC-5);
  // brand-word «Color Lines 98 Online» — имя собственное, остаётся литералом (AC-8).
  // Все локализованные a11y + видимые подписи перерисовываются при смене языка через
  // applyRailLocale() (subscribe ниже) — БЕЗ пересоздания singleton (AC-2).
  const rail = document.createElement('aside');
  rail.className = 'nav-rail';
  rail.setAttribute('aria-label', t('game.rail.navAria'));

  rail.innerHTML = `
    <div class="nav-brand">
      <span class="nav-brand-mark">${BRAND_LOGO_SVG}</span>
      <span class="nav-brand-word">Color Lines 98 Online</span>
    </div>
    <nav class="nav-list" aria-label="${escapeHtml(t('game.rail.sectionsAria'))}"></nav>
    <div class="nav-foot"></div>
  `;

  const listEl = rail.querySelector<HTMLElement>('.nav-list')!;
  const footEl = rail.querySelector<HTMLElement>('.nav-foot')!;

  // --- Пункты навигации. ---
  // Подписи + disabled-title резолвятся через t(item.labelKey) / t('game.rail.comingSoon')
  // (T-I18N-RAIL AC-1). Сохраняем (узел-подписи + ключ) и (disabled-кнопки), чтобы
  // applyRailLocale() перерисовал их при смене языка по сохранённым ключам (AC-2),
  // без пересоздания рейла-singleton.
  let currentActive = opts.activeId ?? null;
  const itemEls = new Map<string, HTMLButtonElement>();
  /** Узлы-подписи пунктов + их каталог-ключи (для перерисовки при смене языка). */
  const labelNodes: { el: HTMLElement; key: string }[] = [];
  /** disabled-кнопки (их title = coming-soon — тоже локализуется). */
  const disabledBtns: HTMLButtonElement[] = [];

  for (const item of opts.items) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nav-item';
    btn.dataset.id = item.id;
    if (item.disabled) btn.classList.add('nav-item--disabled');
    if (item.id === currentActive) btn.classList.add('nav-item--active');
    btn.innerHTML = `
      <span class="nav-item-icon">${svg(ICONS[item.icon], { filled: item.icon === 'play' })}</span>
      <span class="nav-item-label">${escapeHtml(t(item.labelKey))}</span>
      ${item.badge ? `<span class="nav-item-badge">${escapeHtml(item.badge)}</span>` : ''}
    `;
    const labelEl = btn.querySelector<HTMLElement>('.nav-item-label')!;
    labelNodes.push({ el: labelEl, key: item.labelKey });
    if (item.disabled) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      // coming-soon — через hover-title, НЕ через layout-пилюлю (пилюля отъедала ширину
      // и резала подпись settings-пункта, owner-directive 2026-05-30: «убрать лишние
      // символы, расширяющие рейл»). Серый цвет уже сигналит «недоступно».
      btn.title = t('game.rail.comingSoon');
      disabledBtns.push(btn);
    } else {
      btn.addEventListener('click', () => {
        void (async () => {
          if (opts.beforeNavigate && !(await opts.beforeNavigate(item.id))) return;
          setActive(item.id);
          closeDrawer();
          item.onSelect?.();
        })();
      });
    }
    listEl.appendChild(btn);
    itemEls.set(item.id, btn);
  }

  // --- Account-slot (§2): ОДИН state-driven слот СВЕРХУ рейла, сразу под брендом
  // (owner-directive 2026-06-12: вход/чип — первое, что видит игрок, под «Lines 98»).
  // Прибит под брендом (flex:0 0 auto), не скроллится. Гость → login-пилюля
  // (клик → onLogin → auth-modal); залогинен → account-chip (аватар + ник + меню). ---
  const brandEl = rail.querySelector<HTMLElement>('.nav-brand')!;
  // Бренд-логотип кликабелен → лендинг (ADR-0020), если задан onBrandSelect.
  if (opts.onBrandSelect) {
    brandEl.classList.add('nav-brand--link');
    brandEl.setAttribute('role', 'link');
    brandEl.tabIndex = 0;
    brandEl.setAttribute('aria-label', t('game.rail.brandHomeAria'));
    const go = (): void => {
      closeDrawer();
      opts.onBrandSelect!();
    };
    brandEl.addEventListener('click', go);
    brandEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        go();
      }
    });
  }
  const accountSlot = document.createElement('div');
  accountSlot.className = 'nav-account-slot';
  brandEl.insertAdjacentElement('afterend', accountSlot);

  let chip: AccountChipHandle | null = null;
  let accountState: NavRailAccountIdentity | null = opts.account ?? null;

  /**
   * Перерисовать account-slot из текущего `accountState` (§2.3). Снимает прошлый chip/
   * пилюлю, монтирует актуальный вид. Колбэки — из `opts.accountCallbacks`.
   */
  function renderAccountSlot(): void {
    if (chip) {
      chip.unmount();
      chip = null;
    }
    accountSlot.innerHTML = '';
    const cb = opts.accountCallbacks;

    if (accountState) {
      // Залогинен → account-chip (переиспользуемый примитив).
      chip = mountAccountChip(accountSlot, {
        playerId: accountState.playerId,
        displayName: accountState.displayName,
        onLogout: () => cb?.onLogout(),
      });
    } else {
      // Гость → accent login-пилюля (примитив pill-button — общий с logout-пилюлей
      // чипа). Подпись из каталога t('game.rail.login') (AC-1). renderAccountSlot()
      // вызывается и при смене языка (applyRailLocale) → CTA перерисовывается на новый
      // язык без пересоздания рейла (AC-2).
      const loginBtn = createPillButton({
        icon: svg(ICON_LOGIN),
        label: t('game.rail.login'),
        className: 'nav-account-login',
        onClick: () => {
          closeDrawer(); // модалка сама поверх; drawer на узком свернуть.
          cb?.onLogin();
        },
      }).element;
      accountSlot.appendChild(loginBtn);
    }
  }
  renderAccountSlot();

  // --- Глобальный переключатель языка (T-I18N-2 / ADR-0021 D-7): выпадающий список
  // ПОД логотипом (под account-slot), singleton — ОДИН на все экраны с рейлом.
  // Список строится из ЕДИНОГО config-источника (UI_LANGS + LANG_LABELS + LANG_FLAG +
  // HREFLANG_TAG) — НЕ хардкод (AC-4). Флаги — SVG по ISO-коду (LANG_FLAG → flagSvg,
  // AC-5). DOM/поведение/a11y/клавиатура — переиспользуемая ветка lib/nav-lang.
  // Раскрытие наследует состояние рейла: на свёрнутом рейле (☰) список раскрывается
  // ВНУТРИ уже выехавшего drawer'а (attachDrawer-механика рейла) inline — по ГЕОМЕТРИИ,
  // НЕ по width-@media (AC-10). Смена языка реактивна (game-store setLocale, БЕЗ reload,
  // AC-6) + lazy-каталог (i18n setLocale) + на контент-фазе URL следует за языком через
  // ствол navigateContent (AC-7) — НЕ свой прямой History-вызов в контроле. ---
  const langOptions = UI_LANGS.map((code) => ({
    code,
    label: LANG_LABELS[code],
    flagSvg: flagSvg(LANG_FLAG[code]),
    hreflang: HREFLANG_TAG[code],
  }));
  // Язык БРАУЗЕРА (navigator.language → UI_LANGS, иначе en-дефолт) — ФИКСИРОВАННАЯ первая
  // строка списка языков (owner 2026-06-27): «язык браузера первым, дальше выбирай что
  // хочешь — порядок не меняется». Считается ОДИН раз на монтаже рейла-singleton.
  const browserPin =
    (typeof navigator !== 'undefined' ? localeFromNavigatorLanguage(navigator.language) : null) ??
    DEFAULT_CONTENT_LANG;
  const lang: NavLangHandle = mountNavLang({
    options: langOptions,
    current: useGameStore.getState().locale,
    pinFirst: browserPin,
    onSelect: (code) => {
      closeDrawer(); // на свёрнутом рейле свернуть после выбора
      // Контент-фаза? Запоминаем ТЕКУЩИЙ slug из адресной строки — он авторитетен для
      // «того же slug» (AC-7), даже если страница с этим slug — заглушка/фолбэк (store
      // contentTarget мог зарезолвиться на лендинг). Slug извлекаем ЕДИНЫМ парсером
      // модели B (parseContentPath) — НЕ ручным `.slice(1)`: под моделью B en-путь
      // (`/rules/`) НЕ имеет языкового сегмента, `.slice(1)` отрезал бы сам slug.
      const stBefore = useGameStore.getState();
      const onContent = stBefore.phase === 'content';
      const currentSlug =
        onContent && typeof window !== 'undefined'
          ? parseContentPath(window.location.pathname).slug
          : '';
      // Явный выбор человека → единый ствол applyLocale (lib/i18n/boot-locale):
      // подгружает каталог (t() синхронно) + реактивная store-смена БЕЗ перезагрузки
      // (AC-6) + ПЕРСИСТ (T-I18N-3 D-4: гость → localStorage; залогиненный → ещё и
      // сервер `players.locale`). Тот же ствол, что boot/sync — НЕ дубль логики.
      void applyLocale(code as (typeof UI_LANGS)[number], { persist: true }).finally(() => {
        // На контент-фазе URL следует за языком через ствол navigateContent (тот же
        // slug, History-обновление в стволе) — AC-7/AC-8. На игровых экранах язык в
        // URL не несётся (D-5). Заглушка (10 не-CONTENT) тоже меняет URL → en-фолбэк
        // контента (AC-8) — navigateContent держит запрошенный язык в URL.
        if (onContent) navigateContent(code, currentSlug);
      });
    },
  });
  // Контрол — ПОД account-slot (под логотипом), перед списком направлений.
  accountSlot.insertAdjacentElement('afterend', lang.element);
  // Триггер языка + ВСЕ локализованные подписи/a11y рейла обновляются при любой смене
  // locale (контрол рейла / гео-дефолт / persist) — ТОТ ЖЕ subscribe-приём (T-I18N-2),
  // БЕЗ reload и БЕЗ пересоздания рейла-singleton (T-I18N-RAIL AC-2). Каталог нового
  // языка уже подгружен applyLocale ДО смены store-locale → t() синхронно отдаёт строки.
  // Колбэк fire'ит только на смену стора (после mount) → все const'ы ниже уже init'ы.
  const unsubLocale = useGameStore.subscribe((s) => {
    lang.setCurrent(s.locale);
    applyRailLocale();
    // Длина подписей сменилась с языком → пере-замерить гибкую ширину рейла (text-expansion).
    // rAF-страховка: если для нового языка догружается шрифт, ширина уточнится следующим кадром.
    recompute();
    requestAnimationFrame(recompute);
  });

  // --- Утилиты внизу (шестерёнка settings и т.п.) — иконки без подписей,
  // отделены линией сверху. НЕ проходят через beforeNavigate (это не навигация
  // → оверлей; во время партии ничего не спрашивает). Owner-decision 2026-06-04. ---
  /** Утилитарные кнопки + их каталог-ключи (aria/title локализуются — AC-5). */
  const utilEntries: { el: HTMLElement; key: string }[] = [];
  if (opts.utilities && opts.utilities.length > 0) {
    const utilRow = document.createElement('div');
    utilRow.className = 'nav-utils';
    for (const util of opts.utilities) {
      // Иконка-утилита (⚙ и т.п.) — примитив icon-button, вариант ghost (канон:
      // frameless круг, как × и «?»). aria/title из каталога t(util.ariaLabelKey) (AC-5).
      const ariaLabel = t(util.ariaLabelKey);
      const ub = createIconButton({
        glyph: `<span class="nav-item-icon">${svg(ICONS[util.icon])}</span>`,
        label: ariaLabel,
        variant: 'ghost',
        className: 'nav-util',
        onClick: () => {
          closeDrawer(); // свернуть nav-drawer (на узком) — оверлей утилиты сам поверх
          util.onSelect();
        },
      }).element;
      ub.dataset.id = util.id;
      ub.title = ariaLabel;
      utilRow.appendChild(ub);
      utilEntries.push({ el: ub, key: util.ariaLabelKey });
    }
    footEl.appendChild(utilRow);
  }

  // --- Кнопка ☰ (свёрнутый режим). Примитив icon-button, вариант framed (плавает
  // поверх контента → нужен контраст рамки). nav-burger остаётся только для
  // позиционирования/тени/размера иконки (см. nav-rail.css). ---
  const burger = createIconButton({
    glyph: svg(ICON_BURGER),
    label: t('game.rail.openNav'),
    variant: 'framed',
    className: 'nav-burger',
  }).element;

  // --- Сборка. ---
  host.append(content, scrim, rail, burger);

  // === Видимость / drawer ===
  const shell = host; // классы режима вешаем на host (#app)

  // Drawer-механика (открыть/закрыть/scrim-клик/Esc/aria-expanded) — ОБЩИЙ ствол
  // lib/drawer (тот же модуль, что переиспользует второй рейл-оглавление «Помощи»,
  // AC-13). Анимация/transform остаются в nav-rail.css по классу `nav-drawer-open`
  // (+ prefers-reduced-motion там же). Поведение глобального рейла НЕ меняется —
  // вынесена только общая механика (допустимый рефактор-ствол по тикету).
  const drawer = attachDrawer({
    shell,
    openClass: 'nav-drawer-open',
    scrim,
    toggle: burger,
  });
  const openDrawer = (): void => drawer.open();
  const closeDrawer = (): void => drawer.close();

  burger.addEventListener('click', openDrawer);

  /**
   * Пересчёт inline-vs-collapsed по формуле запаса слева от доски (см. шапку).
   * Доска НИКОГДА не ужимается: рейл показывается inline только если пустой левый
   * отступ доски вмещает рейл + дельту.
   */
  function recompute(): void {
    const root = document.documentElement;
    const cs = getComputedStyle(root);
    const cell = parseFloat(cs.getPropertyValue('--cell-size')) || 40;
    const m = parseFloat(cs.getPropertyValue('--m')) || 8;

    // ── ГИБКАЯ ШИРИНА РЕЙЛА (text-expansion best-practice; owner-directive 2026-06-25) ──
    // Подписи навигации в разных языках разной длины (de/pt/CJK заметно длиннее EN —
    // классический «text expansion»). Вместо обрезки/ужимания шрифта рейл САМ РАСТЁТ под
    // самый длинный ярлык ТЕКУЩЕГО языка (рекомендация №1: flexible auto-sizing container).
    // Замер: ширину диктуют ТОЛЬКО пункты навигации (`.nav-list` — подписи nowrap),
    // НЕ весь рейл. ВАЖНО: бренд-вордмарк «Color Lines 98 Online» под `max-content`
    // развернулся бы в ОДНУ строку и раздул рейл — поэтому его в замер НЕ берём; он
    // (`flex:1 1 auto; min-width:0`) переносится ПО СЛОВАМ и помещается в любую итоговую
    // ширину. Меряем `max-content` списка пунктов + горизонтальные паддинги рейла.
    // floor = прежняя формула (короткие языки не сужаются ниже комфорта), cap =
    // viewport-relative (не конст-потолок, §0.G.5). Пишем `--nav-rail-w` → он питает И
    // ширину рейла (nav-rail.css), И `--rail-clear` (= +δ, global.css) → контент сдвигается
    // синхронно, раскладка доски НЕ разъезжается. Если широкий рейл не влезает в левый
    // запас доски — ниже inline-расчёт свернёт его в ☰ (иконки, без переполнения).
    const rootFs = parseFloat(cs.fontSize) || 16;
    const railFloorPx = Math.max(12 * rootFs, cell * 3.0);
    const railCapPx = Math.max(railFloorPx, root.clientWidth * 0.45);
    let railFlexW = railFloorPx;
    const navListEl = rail.querySelector<HTMLElement>('.nav-list');
    if (navListEl) {
      const prevW = navListEl.style.width;
      navListEl.style.width = 'max-content';
      const itemsW = navListEl.getBoundingClientRect().width;
      navListEl.style.width = prevW;
      const rcs = getComputedStyle(rail);
      const railPadX =
        (parseFloat(rcs.paddingLeft) || 0) + (parseFloat(rcs.paddingRight) || 0);
      railFlexW = Math.min(railCapPx, Math.max(railFloorPx, Math.ceil(itemsW + railPadX) + 1));
    }
    root.style.setProperty('--nav-rail-w', `${railFlexW}px`);

    const gapB = Math.max(1, Math.round(cell * 0.08));
    const fpad = Math.max(4, Math.round(cell * 0.28));
    const boardW = 9 * cell + 8 * gapB + 2 * fpad;
    const leftFree = (root.clientWidth - boardW) / 2;
    // railW измеряем у самого элемента (он отрисован, drawer прячется transform'ом
    // → ширина сохраняется) — без пиннинга констант.
    const railW = rail.getBoundingClientRect().width || 0;
    const inline = leftFree >= railW + m;
    shell.classList.toggle('nav-inline', inline);
    shell.classList.toggle('nav-collapsed', !inline);
    if (inline) closeDrawer();
  }

  window.addEventListener(VIEWPORT_APPLIED_EVENT, recompute);
  window.addEventListener('resize', recompute, { passive: true });
  // ПЕРВЫЙ расчёт — СИНХРОННО, ДО первого пэйнта (owner-bug 2026-06-22: при заходе на
  // главную плашки досок «дёргались/центрировались»). Причина: nav-inline ставился только
  // в rAF → первый кадр контента рисовался во всю ширину (без рейл-клиренса), затем rAF
  // сужал центрированную колонку → видимый сдвиг/ре-центрирование. rail уже в DOM со своей
  // шириной (--cell-size применён инлайн-бутстрапом до mountApp; getBoundingClientRect
  // форсит layout) → класс выставляется до пэйнта, контент сразу «как влитой». rAF оставляем
  // страховкой (дорасчёт после возможной догрузки шрифтов/иконок рейла).
  recompute();
  requestAnimationFrame(recompute);

  // boot-консистентность языка (T-I18N-RAIL AC-2/AC-5): рейл монтируется СИНХРОННО
  // (mountApp), а boot-каталог языка грузится ASYNC (bootInitialLocale fire-and-forget,
  // main.ts) → на момент рендера выше t() мог отдать ключ/прошлый язык. Подстраховка:
  // догрузить каталог текущего store-locale (idempotent, кэш) и перерисовать ВСЕ строки
  // рейла, когда он готов — даже если store.locale не менялся (default 'en' == boot 'en'
  // → subscribe не фаярит). Это НЕ дубль applyLocale (он ставит АКТИВНЫЙ язык + store +
  // персист; здесь — только гарантия, что подписи рейла соответствуют store.locale).
  void setLocale(useGameStore.getState().locale).then(() => {
    applyRailLocale();
    // подписи приехали из ASYNC-каталога → пере-замерить гибкую ширину рейла под них.
    recompute();
    requestAnimationFrame(recompute);
  });

  function setActive(id: string | null): void {
    if (currentActive) itemEls.get(currentActive)?.classList.remove('nav-item--active');
    currentActive = id;
    if (id) itemEls.get(id)?.classList.add('nav-item--active');
  }

  function setAccountState(identity: NavRailAccountIdentity | null): void {
    accountState = identity;
    renderAccountSlot();
  }

  /**
   * Выставить/снять dot-индикатор «есть новое» на пункте `id` (helper-unlock A2, AC-11/12/13).
   * Dot = вариант существующего бейджа (`.nav-item-badge--dot`) на самой кнопке-пункте →
   * виден и в inline-, и в collapsed-рейле (кнопка та же). aria-добавка (AC-13): пока dot
   * активен, у кнопки `aria-label` = «<подпись>, <ariaLabel>» (смысл не только цветом); при
   * снятии `aria-label` убирается (accessible-имя снова = подпись пункта). Идемпотентно.
   */
  function setItemBadge(id: string, kind: 'dot' | null, ariaLabel?: string): void {
    const btn = itemEls.get(id);
    if (!btn) return;
    const existing = btn.querySelector<HTMLElement>('.nav-item-badge--dot');
    if (kind === 'dot') {
      if (!existing) {
        const dot = document.createElement('span');
        dot.className = 'nav-item-badge nav-item-badge--dot';
        dot.setAttribute('aria-hidden', 'true'); // смысл несёт aria-label кнопки (AC-13)
        btn.appendChild(dot);
      }
      // aria-добавка: accessible-имя пункта = подпись + «есть новые помощники» (AC-13).
      const labelText = btn.querySelector<HTMLElement>('.nav-item-label')?.textContent ?? '';
      btn.setAttribute('aria-label', ariaLabel ? `${labelText}, ${ariaLabel}` : labelText);
    } else {
      if (existing) existing.remove();
      // Снять aria-добавку → accessible-имя снова = текст подписи (default).
      btn.removeAttribute('aria-label');
    }
  }

  /**
   * Перерисовать ВСЕ локализованные строки рейла из каталога активного языка
   * (T-I18N-RAIL AC-2/AC-5). Вызывается из subscribe на store-locale — рейл-singleton
   * НЕ пересоздаётся: переписываются только текст-узлы/атрибуты по сохранённым ключам.
   * Покрывает: подписи пунктов (labelNodes), title disabled-пунктов (coming-soon), CTA
   * входа (login — через renderAccountSlot, который строит пилюлю из t()), aria рейла/
   * nav-list, accessible-имя ☰, aria/title утилит, aria бренд-ссылки. brand-word —
   * имя собственное, НЕ трогаем (AC-8).
   */
  function applyRailLocale(): void {
    // Подписи пунктов навигации.
    for (const { el, key } of labelNodes) el.textContent = t(key);
    // title disabled-пунктов (coming-soon).
    for (const b of disabledBtns) b.title = t('game.rail.comingSoon');
    // CTA входа гостя (login) — пилюля строится из t(); перерисовать слот.
    renderAccountSlot();
    // a11y рейла / списка разделов.
    rail.setAttribute('aria-label', t('game.rail.navAria'));
    listEl.setAttribute('aria-label', t('game.rail.sectionsAria'));
    // accessible-имя ☰ (aria-label у icon-button; title не ставится примитивом).
    burger.setAttribute('aria-label', t('game.rail.openNav'));
    // aria/title утилит (шестерёнка settings и т.п.).
    for (const { el, key } of utilEntries) {
      const label = t(key);
      el.setAttribute('aria-label', label);
      el.title = label;
    }
    // aria бренд-ссылки (имя бренда + локализованный хвост «— to home»/«— на главную»).
    if (opts.onBrandSelect) brandEl.setAttribute('aria-label', t('game.rail.brandHomeAria'));
  }

  return {
    contentSlot: content,
    setActive,
    setAccountState,
    setItemBadge,
    detach(): void {
      window.removeEventListener(VIEWPORT_APPLIED_EVENT, recompute);
      window.removeEventListener('resize', recompute);
      drawer.detach();
      lang.detach();
      unsubLocale();
      chip?.unmount();
      shell.classList.remove('nav-inline', 'nav-collapsed');
      host.innerHTML = '';
    },
  };
}

// =============================================================================
// Helpers
// =============================================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (ch) =>
    ch === '&' ? '&amp;' : ch === '<' ? '&lt;' : ch === '>' ? '&gt;' : '&quot;',
  );
}
